<script setup lang="ts">
import { computed, onUnmounted, ref, useSlots } from "vue";
import { toTooltipAnchor, useTooltip } from "@/stores/useTooltip";
import InfoCircle from "./InfoCircle.vue";

/**
 * The ⓘ affordance for a short hover hint. Renders the icon, or wraps whatever you put in its slot, and
 * publishes the text into the global tooltip store for `Tooltip.vue` to draw.
 *
 * For a hint on an element you already render, prefer `v-tooltip` — it needs no extra element. For
 * documentation too long for a tooltip, use `InfoModalButton`, which used to be a third mode of this
 * component (`markdownPath`) despite never showing a tooltip at all.
 */

const props = withDefaults(
  defineProps<{
    tooltip?: string;
    tooltipTitle?: string;
    inverted?: boolean;
    /** `cursor` trails the pointer; `top` anchors above the icon, for when the pointer would cover it. */
    position?: "cursor" | "top";
  }>(),
  { inverted: false, position: "cursor" }
);

const tooltipStore = useTooltip();
const slots = useSlots();

const isTooltipMode = computed(() => !!props.tooltip);
const hasSlot = computed(() => !!slots.default);
const useAnchored = computed(() => props.position === "top");

const triggerRef = ref<HTMLElement | null>(null);
const isShowingTooltip = ref(false);

// Both positions go through the one global renderer. `position="top"` used to render its own pill here —
// a second renderer with its own styling and its own scroll/resize listeners — which the store's
// element-anchored `top` placement replaced. The rect is measured per show, so scrolling no longer needs
// listening to: the pill is only up while the pointer is on the trigger.
const handleMouseEnter = () => {
  if (!isTooltipMode.value || !props.tooltip) return;

  const tooltipHtml = `
            <div class='flex max-w-xs flex-col text-white'>
                ${props.tooltipTitle ? `<h1 class='t-sh4 mb-px-8'>${props.tooltipTitle}</h1>` : ""}
              <p class='t-xs wrap-break-word'>${props.tooltip}</p>
            </div>
        `;

  tooltipStore.showTooltip(tooltipHtml, {
    placement: useAnchored.value ? "top" : "cursor",
    anchorRect: useAnchored.value && triggerRef.value ? toTooltipAnchor(triggerRef.value.getBoundingClientRect()) : null,
  });
  isShowingTooltip.value = true;
};

const handleMouseLeave = () => {
  if (!isTooltipMode.value) return;
  tooltipStore.hideTooltip();
  isShowingTooltip.value = false;
};

onUnmounted(() => {
  // Leaving with the pill up (a row unmounting mid-hover) never fires mouseleave.
  if (isShowingTooltip.value) tooltipStore.hideTooltip();
});
</script>

<template>
  <span
    v-if="isTooltipMode && hasSlot"
    ref="triggerRef"
    role="img"
    aria-label="More information"
    class="inline-block cursor-default"
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave">
    <slot />
  </span>
  <span
    v-else-if="isTooltipMode"
    ref="triggerRef"
    role="img"
    aria-label="More information"
    class="flex cursor-pointer p-1 transition-colors"
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave">
    <InfoCircle :inverted="inverted" />
  </span>
</template>
